<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="520px"
    persistent
    transition="dialog-transition"
  >
    <v-card class="elevation-6 rounded-lg mainfont" style="overflow: hidden;">
      <!-- Header -->
      <v-card-title class="py-4 px-5 d-flex align-center justify-space-between" style="background: linear-gradient(135deg, #13736f 0%, #283e51 100%); color: white;">
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-filter-variant</v-icon>
          <span style="font-size: 18px; font-weight: 700; letter-spacing: 0.5px;">Filter Applications</span>
        </div>
        <v-btn icon dark small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Body Form -->
      <v-card-text class="pt-6 pb-2 px-6">
        <v-layout wrap>
          <!-- Division Selection -->
          <v-flex xs12 class="mb-4">
            <label class="filter-field-label">
              <v-icon small color="#13736f" class="mr-1">mdi-domain</v-icon>
              Division
            </label>
            <v-autocomplete
              dense
              outlined
              clearable
              :disabled="isDivisionScoped"
              :items="divisionList"
              item-text="officeName"
              item-value="_id"
              v-model="selectedDivisionId"
              placeholder="Select Division"
              color="#13736f"
              class="custom-filter-select"
              hide-details
              @change="onDivisionChange"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">{{ item.officeName }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <div v-if="isDivisionScoped" class="caption grey--text text--darken-1 mt-1">
              <v-icon x-small color="grey">mdi-lock-outline</v-icon> Scoped to your assigned division
            </div>
          </v-flex>

          <!-- Range Selection -->
          <v-flex xs12 class="mb-4">
            <label class="filter-field-label">
              <v-icon small color="#13736f" class="mr-1">mdi-map-marker-path</v-icon>
              Range
            </label>
            <v-autocomplete
              dense
              outlined
              clearable
              :disabled="!isDivisionScoped && !selectedDivisionId"
              :items="rangeList"
              item-text="officeName"
              item-value="_id"
              v-model="selectedRangeId"
              placeholder="Select Range"
              color="#13736f"
              class="custom-filter-select"
              hide-details
              @change="onRangeChange"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">{{ item.officeName }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-flex>

          <!-- Section Selection -->
          <v-flex xs12 class="mb-4" v-if="sectionList && sectionList.length > 0">
            <label class="filter-field-label">
              <v-icon small color="#13736f" class="mr-1">mdi-vector-polyline</v-icon>
              Section
            </label>
            <v-autocomplete
              dense
              outlined
              clearable
              :disabled="!selectedRangeId && !isDivisionScoped"
              :items="sectionList"
              item-text="officeName"
              item-value="_id"
              v-model="selectedSectionId"
              placeholder="Select Section"
              color="#13736f"
              class="custom-filter-select"
              hide-details
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">{{ item.officeName }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-flex>

          <!-- Camp Type Filter (All / Free / Paid) -->
          <v-flex xs12 class="mb-2">
            <label class="filter-field-label mb-2 d-block">
              <v-icon small color="#13736f" class="mr-1">mdi-tag-multiple</v-icon>
              Camp Type
            </label>
            <div class="d-flex align-center camp-type-toggle-group">
              <v-btn
                small
                depressed
                :class="selectedCampType === '' ? 'camp-type-btn-active' : 'camp-type-btn'"
                @click="selectedCampType = ''"
              >
                All Camps
              </v-btn>
              <v-btn
                small
                depressed
                :class="selectedCampType === 'paid' ? 'camp-type-btn-active' : 'camp-type-btn'"
                @click="selectedCampType = 'paid'"
              >
                <v-icon x-small left>mdi-currency-inr</v-icon>
                Paid Camp
              </v-btn>
              <v-btn
                small
                depressed
                :class="selectedCampType === 'free' ? 'camp-type-btn-active' : 'camp-type-btn'"
                @click="selectedCampType = 'free'"
              >
                <v-icon x-small left>mdi-gift-outline</v-icon>
                Free Camp
              </v-btn>
            </div>
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Actions -->
      <v-card-actions class="py-3 px-6 bg-light d-flex justify-space-between align-center">
        <v-btn
          text
          color="red darken-2"
          class="font-weight-bold text-capitalize"
          @click="resetFilters"
        >
          <v-icon small left>mdi-restore</v-icon>
          Reset Filters
        </v-btn>

        <div class="d-flex align-center" style="gap: 12px;">
          <v-btn
            outlined
            color="grey darken-2"
            class="text-capitalize font-weight-medium px-4 mr-1"
            @click="close"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            color="#13736f"
            dark
            class="text-capitalize font-weight-bold px-5"
            @click="applyFilters"
          >
            <v-icon small left>mdi-check</v-icon>
            Apply Filters
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

export default {
  name: "ApplicationFilterDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    userRole: {
      type: String,
      default: "admin",
    },
    initialFilters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      divisionList: [],
      rangeList: [],
      sectionList: [],
      selectedDivisionId: null,
      selectedRangeId: null,
      selectedSectionId: null,
      selectedCampType: "",
      myDivision: null,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isDivisionScoped() {
      const role = (this.userRole || localStorage.getItem("userType") || "").toLowerCase();
      return role === "division" || role === "clerk" || role === "superint";
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.syncInitialFilters();
        this.initOffices();
      }
    },
    initialFilters: {
      deep: true,
      handler() {
        this.syncInitialFilters();
      },
    },
  },
  mounted() {
    this.syncInitialFilters();
    this.initOffices();
  },
  methods: {
    syncInitialFilters() {
      if (this.initialFilters) {
        this.selectedDivisionId = this.initialFilters.divisionid || null;
        this.selectedRangeId = this.initialFilters.rangeid || null;
        this.selectedSectionId = this.initialFilters.sectionid || null;
        this.selectedCampType = this.initialFilters.campType || "";
      }
    },
    initOffices() {
      if (this.isDivisionScoped) {
        this.fetchScopedDivisionOffices();
      } else {
        this.fetchAllDivisions();
        if (this.selectedDivisionId) {
          this.fetchRangesForDivision(this.selectedDivisionId);
        }
      }
    },
    fetchScopedDivisionOffices() {
      axios({
        method: "GET",
        url: "/get/my-division-offices",
        headers: {
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data && response.data.status && response.data.data) {
            const data = response.data.data;
            if (data.division) {
              this.myDivision = data.division;
              this.divisionList = [data.division];
              this.selectedDivisionId = data.division._id;
            }
            this.rangeList = data.ranges || [];
            this.sectionList = data.sections || [];
          }
        })
        .catch((err) => {
          console.error("Error fetching scoped division offices:", err);
        });
    },
    fetchAllDivisions() {
      axios({
        method: "GET",
        url: "/get/all/divisions",
        headers: {
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data && response.data.status && Array.isArray(response.data.data)) {
            this.divisionList = response.data.data;
          }
        })
        .catch((err) => {
          console.error("Error fetching divisions:", err);
        });
    },
    onDivisionChange(divisionId) {
      this.selectedRangeId = null;
      this.selectedSectionId = null;
      this.rangeList = [];
      this.sectionList = [];
      if (divisionId) {
        this.fetchRangesForDivision(divisionId);
      }
    },
    fetchRangesForDivision(divisionId) {
      axios({
        method: "POST",
        url: "/get/ranges/by-division",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          divisionid: divisionId,
        },
      })
        .then((response) => {
          if (response.data && response.data.status && Array.isArray(response.data.data)) {
            this.rangeList = response.data.data;
          }
        })
        .catch((err) => {
          console.error("Error fetching ranges for division:", err);
        });
    },
    onRangeChange(rangeId) {
      this.selectedSectionId = null;
      this.sectionList = [];
      if (rangeId) {
        this.fetchSectionsForRange(rangeId);
      }
    },
    fetchSectionsForRange(rangeId) {
      axios({
        method: "POST",
        url: "/get/sections/by-range",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          rangeid: rangeId,
        },
      })
        .then((response) => {
          if (response.data && response.data.status && Array.isArray(response.data.data)) {
            this.sectionList = response.data.data;
          }
        })
        .catch((err) => {
          console.error("Error fetching sections for range:", err);
        });
    },
    applyFilters() {
      const selectedDivision = this.divisionList.find((d) => d._id === this.selectedDivisionId);
      const selectedRange = this.rangeList.find((r) => r._id === this.selectedRangeId);
      const selectedSection = this.sectionList.find((s) => s._id === this.selectedSectionId);

      const filterPayload = {
        divisionid: this.selectedDivisionId || null,
        divisionName: selectedDivision ? selectedDivision.officeName : "",
        rangeid: this.selectedRangeId || null,
        rangeName: selectedRange ? selectedRange.officeName : "",
        sectionid: this.selectedSectionId || null,
        sectionName: selectedSection ? selectedSection.officeName : "",
        campType: this.selectedCampType || "",
      };

      this.$emit("apply", filterPayload);
      this.close();
    },
    resetFilters() {
      if (!this.isDivisionScoped) {
        this.selectedDivisionId = null;
        this.rangeList = [];
      }
      this.selectedRangeId = null;
      this.selectedSectionId = null;
      this.sectionList = [];
      this.selectedCampType = "";

      this.$emit("reset");
      this.close();
    },
    close() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.filter-field-label {
  font-size: 13.5px;
  font-weight: 700;
  color: #37474f;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.custom-filter-select >>> .v-input__control {
  min-height: 40px !important;
}

.custom-filter-select >>> .v-input__slot {
  border-radius: 8px !important;
  background-color: #f8fafc !important;
  border-color: #cfd8dc !important;
}

.camp-type-toggle-group {
  gap: 8px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 8px;
}

.camp-type-btn {
  flex: 1;
  background: transparent !important;
  color: #475569 !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  text-transform: none !important;
  height: 36px !important;
}

.camp-type-btn-active {
  flex: 1;
  background: #13736f !important;
  color: white !important;
  font-weight: 700 !important;
  border-radius: 6px !important;
  text-transform: none !important;
  height: 36px !important;
  box-shadow: 0 2px 6px rgba(19, 115, 111, 0.3) !important;
}

.bg-light {
  background-color: #f8fafc;
}
</style>
